import {
  generateCachebuster,
  INVISO_FIXED_TRACKING_PARAMS,
  INVISO_TRACKING_PIXEL_BASE_URL,
  invisoTrackingPixelConfig,
} from "common/config/inviso-tracking-pixels";
import { PublicationContext } from "common/context/Publication";
import { publicationTypes } from "common/context/Publication/PublicationContext";
import { FC, useContext, useEffect, useRef } from "react";

const InvisoTrackingPixel: FC = () => {
  const { publication } = useContext<publicationTypes>(PublicationContext);
  const hasFired = useRef(false);
  const trackingUrl = useRef<string | null>(null);

  // Get the Inviso tracking configuration for the current publication
  const invisoConfig = invisoTrackingPixelConfig[publication];

  // Build the Inviso tracking pixel URL only once per page load
  useEffect(() => {
    // Only proceed if we have config and haven't fired yet
    if (!invisoConfig || hasFired.current) {
      return;
    }

    const invisoTrackingUrl = new URL(INVISO_TRACKING_PIXEL_BASE_URL);
    invisoTrackingUrl.searchParams.set("i", invisoConfig.i);
    invisoTrackingUrl.searchParams.set("c", invisoConfig.c);
    invisoTrackingUrl.searchParams.set("t", INVISO_FIXED_TRACKING_PARAMS.t);
    invisoTrackingUrl.searchParams.set("cg", INVISO_FIXED_TRACKING_PARAMS.cg);
    invisoTrackingUrl.searchParams.set("r", INVISO_FIXED_TRACKING_PARAMS.r);
    invisoTrackingUrl.searchParams.set(
      "rdir",
      INVISO_FIXED_TRACKING_PARAMS.rdir
    );
    invisoTrackingUrl.searchParams.set("c0", INVISO_FIXED_TRACKING_PARAMS.c0);
    invisoTrackingUrl.searchParams.set("c1", invisoConfig.c1);

    // Add cachebuster to bypass CloudFlare caching
    invisoTrackingUrl.searchParams.set("cb", generateCachebuster());

    trackingUrl.current = invisoTrackingUrl.toString();
    hasFired.current = true;
  }, [invisoConfig]);

  // If no Inviso tracking config is found for this publication, don't render anything
  if (!invisoConfig) {
    console.warn(
      `No Inviso tracking pixel configuration found for publication: ${publication}`
    );
    return null;
  }

  // eg. <img style="display: none" src="https://inviso.rampanel.com/ram/Inviso?i=40066&c=62784&t=6&cg=0&r=1&rdir=1&c0=Tindle_Newspapers_Ltd&c1=Abergavenny_Chronicle_Online"/>

  // Only render if we have a tracking URL (prevents double-firing)
  if (!trackingUrl.current) {
    return null;
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      style={{ display: "none" }}
      src={trackingUrl.current}
      alt=""
      aria-hidden="true"
    />
  );
};

export default InvisoTrackingPixel;
